import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Plus, Briefcase, Users, Mail, Phone } from 'lucide-react';

const Opportunities = () => {
  const { user, token } = React.useContext(AuthContext);
  const [opportunities, setOpportunities] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [applicantsDialogOpen, setApplicantsDialogOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [formData, setFormData] = useState({
    type: 'job',
    title: '',
    description: ''
  });

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/opportunities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpportunities(response.data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/opportunities`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Opportunity posted successfully!');
      setIsDialogOpen(false);
      setFormData({ type: 'job', title: '', description: '' });
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post opportunity');
    }
  };

  const handleApply = async (oppId) => {
    try {
      await axios.post(`${API}/opportunities/${oppId}/apply`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application submitted!');
      fetchOpportunities();
    } catch (error) {
      toast.error('Failed to apply');
    }
  };

  const handleViewApplicants = async (opp) => {
    try {
      setSelectedOpportunity(opp);
      const response = await axios.get(`${API}/opportunities/${opp.id}/applicants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplicants(response.data);
      setApplicantsDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load applicants');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="opportunities-page">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Opportunities Board
          </h1>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-rose-500 hover:bg-rose-600 flex items-center gap-2" data-testid="post-opportunity-button">
                <Plus className="w-4 h-4" />
                Post Opportunity
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="post-opportunity-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Post New Opportunity</DialogTitle>
                <DialogDescription>Share a job or collaboration opportunity</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="opportunity-form">
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger data-testid="opportunity-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="job" data-testid="type-job">Job Opening</SelectItem>
                    <SelectItem value="collaboration" data-testid="type-collaboration">Collaboration</SelectItem>
                  </SelectContent>
                </Select>
                <Input 
                  name="title" 
                  placeholder="Title" 
                  value={formData.title} 
                  onChange={handleChange} 
                  required
                  data-testid="opportunity-title-input"
                />
                <Textarea 
                  name="description" 
                  placeholder="Description" 
                  value={formData.description} 
                  onChange={handleChange} 
                  required
                  rows={4}
                  data-testid="opportunity-description-input"
                />
                <Button type="submit" className="w-full bg-rose-500 hover:bg-rose-600" data-testid="opportunity-submit-button">Post Opportunity</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {opportunities.map((opp) => {
            const hasApplied = opp.applicants.includes(user?.id);
            const isOwner = opp.posted_by === user?.id;
            return (
              <Card key={opp.id} className="glass hover:shadow-xl transition-all" data-testid={`opportunity-card-${opp.id}`}>
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    {opp.type === 'job' ? <Briefcase className="w-5 h-5 text-rose-500" /> : <Users className="w-5 h-5 text-rose-500" />}
                    <span className="text-xs px-3 py-1 bg-rose-100 text-rose-700 rounded-full font-medium">{opp.type}</span>
                  </div>
                  <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>{opp.title}</CardTitle>
                  <CardDescription>Posted by {opp.poster_name}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-700">{opp.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{opp.applicants.length} applicants</span>
                    <div className="flex gap-2">
                      {isOwner ? (
                        <Button 
                          onClick={() => handleViewApplicants(opp)} 
                          variant="outline"
                          size="sm"
                          className="text-rose-600 border-rose-300"
                        >
                          View Applicants
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleApply(opp.id)} 
                          disabled={hasApplied}
                          className={hasApplied ? 'bg-gray-400' : 'bg-rose-500 hover:bg-rose-600'}
                          size="sm"
                          data-testid={`apply-${opp.id}`}
                        >
                          {hasApplied ? 'Applied' : 'Apply'}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {opportunities.length === 0 && (
          <div className="text-center py-12" data-testid="no-opportunities">
            <p className="text-gray-500">No opportunities posted yet</p>
          </div>
        )}

        {/* Applicants Dialog */}
        <Dialog open={applicantsDialogOpen} onOpenChange={setApplicantsDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Applicants for: {selectedOpportunity?.title}
              </DialogTitle>
              <DialogDescription>
                {applicants.length} {applicants.length === 1 ? 'person has' : 'people have'} applied to this opportunity
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {applicants.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No applicants yet</p>
              ) : (
                applicants.map((applicant) => (
                  <Card key={applicant.id} className="bg-white">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        {applicant.avatar_url && (
                          <img 
                            src={applicant.avatar_url} 
                            alt={applicant.full_name} 
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900">{applicant.full_name}</h3>
                          {applicant.business_name && (
                            <p className="text-sm text-gray-600">{applicant.business_name}</p>
                          )}
                          {applicant.industry && (
                            <Badge variant="outline" className="mt-2">{applicant.industry}</Badge>
                          )}
                          
                          <div className="mt-3 space-y-1">
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <Mail className="w-4 h-4 text-gray-500" />
                              <a href={`mailto:${applicant.email}`} className="hover:text-rose-600">
                                {applicant.email}
                              </a>
                            </div>
                            {applicant.phone && (
                              <div className="flex items-center gap-2 text-sm text-gray-700">
                                <Phone className="w-4 h-4 text-gray-500" />
                                <a href={`tel:${applicant.phone}`} className="hover:text-rose-600">
                                  {applicant.phone}
                                </a>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-4 flex gap-2">
                            <Button
                              onClick={() => window.location.href = `/profile/${applicant.id}`}
                              size="sm"
                              variant="outline"
                              className="text-rose-600 border-rose-300"
                            >
                              View Profile
                            </Button>
                            <Button
                              onClick={() => window.location.href = `mailto:${applicant.email}`}
                              size="sm"
                              className="bg-rose-600 hover:bg-rose-700"
                            >
                              Contact
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default Opportunities;